import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatDate,
  formatTime,
  TV_BIRTHDAY_HOUR,
  TV_BIRTHDAY_MINUTES_MAX,
  TV_WELCOME_MODES,
  TV_WELCOME_TEXT_MAX,
  TV_WELCOME_URL_MAX,
  youtubeEmbedUrl,
  type TvOverride,
  type TvWelcomeAdmin,
  type TvWelcomeMode,
  type TvWelcomeSettings as Settings,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import {
  Alert,
  Badge,
  Button,
  CardBody,
  CollapsibleCard,
  Input,
  Label,
  Select,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { TvFrameView, TvPreview, TvWelcomeView } from '@/pages/TvDashboard';

/**
 * The welcome screen on /admin/tv-dashboard: a text or a YouTube video that takes over all three
 * wall screens until its end time or until Stop, and the birthday greeting from OneCrew at noon -
 * see tv-welcome.ts in the API. Stored as `DASHBOARD_TV_WELCOME`. The screens pick a change up
 * within ten seconds.
 */

const WELCOME_KEY = ['admin', 'tv-dashboard', 'welcome'] as const;

const MODE_LABELS: Record<TvWelcomeMode, string> = {
  text: 'Tekst',
  youtube: 'YouTube-video',
};

const SHOWING: Record<Exclude<TvOverride['kind'], 'birthday'>, string> = {
  welcome: 'teksten',
  youtube: 'YouTube-videoen',
};

/** `2026-10-23T14:30` for an `<input type="datetime-local">`, in the browser's own time. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function TvWelcomeSettings() {
  const queryClient = useQueryClient();
  const canEdit = useAuth().can('tv_dashboard.manage');
  const [draft, setDraft] = useState<Settings | null>(null);
  const [message, setMessage] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);

  const query = useQuery({
    queryKey: WELCOME_KEY,
    queryFn: () => api.get<TvWelcomeAdmin>('/tv-dashboard/welcome'),
    // What shows now changes on its own - at noon and at the end time.
    refetchInterval: 60_000,
  });

  const saved = query.data?.settings;
  const current = draft ?? saved;

  const save = useMutation({
    mutationFn: (next: Settings) => api.put<TvWelcomeAdmin>('/tv-dashboard/welcome', next),
    onMutate: () => setMessage(null),
    onSuccess: (result, next) => {
      queryClient.setQueryData(WELCOME_KEY, result);
      setDraft(null);
      setMessage({
        tone: 'info',
        text:
          next.active && !running
            ? `${next.mode === 'text' ? 'Teksten' : 'Videoen'} vises på alle skærme inden for ti sekunder.`
            : !next.active && running
              ? 'Det fælles indhold er taget af. Skærmene viser deres tal igen inden for ti sekunder.'
              : 'Gemt. Skærmene har ændringen inden for ti sekunder.',
      });
    },
    onError: (cause) =>
      setMessage({
        tone: 'error',
        text: cause instanceof ApiError ? cause.message : 'Kunne ikke gemme det fælles indhold.',
      }),
  });

  const edit = (change: Partial<Settings>): void => {
    if (current) setDraft({ ...current, ...change });
  };

  const data = query.data;
  const override = data?.override ?? null;
  /* On and not past its end time - an expired text is off, whatever the flag says. */
  const running =
    data?.settings.active === true &&
    (data.settings.until === null || Date.parse(data.settings.until) > Date.now());

  /* What the preview frames, and the API would accept - null for text or a link it refuses. */
  const frameUrl = current?.mode === 'youtube' ? youtubeEmbedUrl(current.url) : null;
  const canShow = current?.mode === 'text' ? current.text.trim().length > 0 : frameUrl !== null;

  return (
    <CollapsibleCard
      title={
        <>
          Fælles indhold
          {override ? <Badge tone="success">Vises nu</Badge> : null}
        </>
      }
      description={`En stor tekst eller en YouTube-video på alle tre skærme i stedet for tallene - fx når en kunde kommer på besøg. Det står, til sluttidspunktet, eller til der trykkes Stop. På en fødselsdag tager fødselsdagshilsenen over fra kl. ${TV_BIRTHDAY_HOUR}:00.`}
    >
      <CardBody className="space-y-4">
        {message ? <Alert tone={message.tone}>{message.text}</Alert> : null}
        {query.isError ? <Alert tone="error">Kunne ikke hente det fælles indhold.</Alert> : null}

        {!data || !current ? (
          <Skeleton className="h-40" />
        ) : (
          <>
            <Alert tone={override ? 'success' : 'info'}>
              {override
                ? override.kind === 'birthday'
                  ? `Skærmene viser nu fødselsdagshilsenen: "${override.text}".`
                  : `Skærmene viser nu ${SHOWING[override.kind]}${
                      data.settings.until
                        ? ` til ${formatDate(data.settings.until)} kl. ${formatTime(data.settings.until)}`
                        : ', til der trykkes Stop'
                    }.`
                : 'Skærmene viser deres egne tal.'}
            </Alert>

            <fieldset disabled={!canEdit} className="m-0 min-w-0 space-y-4 border-0 p-0">
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="tv-welcome-mode">Vis</Label>
                    <Select
                      id="tv-welcome-mode"
                      className="w-48"
                      value={current.mode}
                      onChange={(event) => edit({ mode: event.target.value as TvWelcomeMode })}
                    >
                      {TV_WELCOME_MODES.map((mode) => (
                        <option key={mode} value={mode}>
                          {MODE_LABELS[mode]}
                        </option>
                      ))}
                    </Select>
                  </div>

                  {current.mode === 'text' ? (
                    <div className="space-y-2">
                      <Label htmlFor="tv-welcome-text">Tekst</Label>
                      <Textarea
                        id="tv-welcome-text"
                        rows={5}
                        maxLength={TV_WELCOME_TEXT_MAX}
                        value={current.text}
                        placeholder={'Velkommen\nKunde A/S'}
                        onChange={(event) => edit({ text: event.target.value })}
                      />
                      <p className="text-xs text-ink-muted">
                        Linjeskift bliver stående. Teksten gøres så stor, som skærmen kan rumme.{' '}
                        {current.text.length}/{TV_WELCOME_TEXT_MAX} tegn.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor="tv-welcome-url">Link til videoen</Label>
                      <Input
                        id="tv-welcome-url"
                        type="url"
                        maxLength={TV_WELCOME_URL_MAX}
                        value={current.url}
                        placeholder="https://www.youtube.com/watch?v=…"
                        onChange={(event) => edit({ url: event.target.value })}
                      />
                      {current.url.trim() && !frameUrl ? (
                        <p className="text-xs text-danger">
                          Det er ikke et link til en YouTube-video.
                        </p>
                      ) : null}
                      <p className="text-xs text-ink-muted">
                        Linket fra adresselinjen eller fra Del-knappen. Videoen starter af sig selv
                        uden lyd og begynder forfra, når den er slut - browsere starter kun en video
                        af sig selv uden lyd.
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="tv-welcome-until">Vis indtil (valgfri)</Label>
                    <Input
                      id="tv-welcome-until"
                      type="datetime-local"
                      className="w-60"
                      value={toLocalInput(current.until)}
                      onChange={(event) =>
                        edit({
                          until: event.target.value
                            ? new Date(event.target.value).toISOString()
                            : null,
                        })
                      }
                    />
                    <p className="text-xs text-ink-muted">Tom: til der trykkes Stop.</p>
                  </div>
                  {canEdit ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        loading={save.isPending}
                        disabled={!canShow}
                        onClick={() => save.mutate({ ...current, active: true })}
                      >
                        {running ? 'Opdater skærmene' : 'Vis på alle skærme'}
                      </Button>
                      {running ? (
                        <Button
                          variant="secondary"
                          loading={save.isPending}
                          onClick={() => save.mutate({ ...current, active: false })}
                        >
                          Stop
                        </Button>
                      ) : null}
                      {draft ? (
                        <Button variant="ghost" onClick={() => setDraft(null)}>
                          Fortryd
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-medium text-ink-muted">Sådan ser det ud</p>
                  <TvPreview ready failed={false} zoomPct={100}>
                    {current.mode === 'text' ? (
                      <TvWelcomeView text={current.text.trim() || 'Velkommen'} />
                    ) : frameUrl ? (
                      <TvFrameView url={frameUrl} />
                    ) : (
                      <p className="flex h-full items-center justify-center text-[48px] text-tv-soft">
                        Sæt et link ind
                      </p>
                    )}
                  </TvPreview>
                </div>
              </div>

              <div className="space-y-2 border-t border-line pt-4">
                <Label htmlFor="tv-birthday-minutes">
                  Fødselsdagshilsen fra kl. {TV_BIRTHDAY_HOUR}:00, i minutter
                </Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    id="tv-birthday-minutes"
                    type="number"
                    className="w-28"
                    min={0}
                    max={TV_BIRTHDAY_MINUTES_MAX}
                    step={5}
                    value={current.birthday_minutes}
                    onChange={(event) =>
                      edit({
                        birthday_minutes: Math.max(0, Math.round(Number(event.target.value) || 0)),
                      })
                    }
                  />
                  {canEdit ? (
                    <Button
                      variant="secondary"
                      disabled={current.birthday_minutes === data.settings.birthday_minutes}
                      loading={save.isPending}
                      onClick={() =>
                        save.mutate({
                          ...data.settings,
                          active: running,
                          birthday_minutes: current.birthday_minutes,
                        })
                      }
                    >
                      Gem
                    </Button>
                  ) : null}
                </div>
                <p className="text-xs text-ink-muted">
                  "Tillykke med fødselsdagen" og det fulde navn fra OneCrew, kun for dem, der har
                  valgt at vise deres fødselsdag. Vinder over det fælles indhold, så længe den står.
                  0 slår hilsenen fra.{' '}
                  {data.birthdays_today.length > 0
                    ? `I dag: ${data.birthdays_today.join(', ')}.`
                    : 'Ingen fødselsdage i dag.'}
                </p>
                {data.error ? <Alert tone="warning">{data.error}</Alert> : null}
              </div>
            </fieldset>
          </>
        )}
      </CardBody>
    </CollapsibleCard>
  );
}
